/**
 * Normalized transport error produced by the axios layer.
 *
 * Backend error bodies use one envelope:
 *   { "code": "validation_error", "detail": "validation_error",
 *     "fields": { "email": [{ "code": "invalid" }] },
 *     "meta": { "current_revision": 7 } }
 *
 * `code` is always the machine code. `detail` mirrors it for one release.
 * `fields` carries field-level codes (never sentences). `meta` carries
 * structured context. The compatibility shim also copies `meta` keys to the
 * response root, and old responses may still use flat field arrays.
 */
export interface FieldErrorDetail {
  code: string;
  params?: Record<string, unknown>;
}

export interface FieldErrors {
  [field: string]: string[];
}

export interface FieldErrorDetails {
  [field: string]: FieldErrorDetail[];
}

export class ApiError extends Error {
  /** HTTP status; 0 means the request never reached the server (network failure). */
  readonly status: number;
  /** The backend error code, when the body carried one; null otherwise. */
  readonly code: string | null;
  /** Field codes by field path; null when the failure is not field-level. */
  readonly fieldErrors: FieldErrors | null;
  /** Field entries including optional params; null when there are none. */
  readonly fieldErrorDetails: FieldErrorDetails | null;
  /** Structured context from `meta`; null when the body carried none. */
  readonly meta: Record<string, unknown> | null;
  /** The raw parsed response body, for callers needing more than the fields above. */
  readonly body: unknown;

  constructor(params: {
    status: number;
    code: string | null;
    fieldErrors: FieldErrors | null;
    fieldErrorDetails?: FieldErrorDetails | null;
    meta?: Record<string, unknown> | null;
    body: unknown;
    message?: string;
  }) {
    super(
      params.message ??
        params.code ??
        `Request failed with status ${String(params.status)}`
    );
    this.name = "ApiError";
    this.status = params.status;
    this.code = params.code;
    this.fieldErrors = params.fieldErrors;
    this.fieldErrorDetails = params.fieldErrorDetails ?? null;
    this.meta = params.meta ?? null;
    this.body = params.body;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
